import type React from "react"
import { useState, useEffect } from "react"
import CreateFranchiseUserForm from "../components/CreateFranchiseUserForm"
import { container } from "../../infrastructure/container"
import type { ApiService } from "../../infrastructure/services/api"
import type { Logger } from "../../infrastructure/services/logger"
import type { User, NewUser, Terminal } from "../../infrastructure/models/user"
import { ClipLoader } from "react-spinners"
import type { SessionService } from "../../infrastructure/services/session"
import { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"
import { useSearch } from "../components/ProtectedLayout"
import { TYPES } from "../models/types"
import { COLORS } from "../constants/colors"
import CreateButton from "../components/button/CreateButton"
import EditButton from "../../presentation/components/button/EditButton"
import DeleteButton from "../../presentation/components/button/DeleteButton"
import ActionButton from "../components/button/ActionButton"
import { ActionButtonVariant } from "../models/enum/ActionButtonVariant"

const MemberDashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null)
  const [members, setMembers] = useState<User[]>([])
  const [showForm, setShowForm] = useState<boolean>(false)
  const [terminals, setTerminals] = useState<Terminal[]>([])
  const [editMode, setEditMode] = useState<boolean>(false)
  const [editedMember, setEditedMember] = useState<User | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isCreatingUser, setIsCreatingUser] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)
  const [successMessage, setSuccessMessage] = useState<string>("")
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [processingMessage, setProcessingMessage] = useState<string>("Loading...")

  const apiService = container.resolve<ApiService>("apiService")
  const sessionService = container.resolve<SessionService>("sessionService")
  const logger = container.resolve<Logger>("logger")
  const { searchQuery } = useSearch()

  const getRoleString = (role: number): string => {
    switch (role) {
      case FranchiseRole.OWNER:
        return "Owner"
      case FranchiseRole.ADMIN:
        return "Admin"
      case FranchiseRole.MANAGER:
        return "Manager"
      case FranchiseRole.USER:
        return "User"
      case FranchiseRole.DELIVERY:
        return "Delivery"
      default:
        return "Unknown"
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const storedUserDetails = sessionService.user

      if (storedUserDetails) {
        try {
          setUser(storedUserDetails)

          const storedTerminals = sessionService.terminals
          if (storedTerminals && Array.isArray(storedTerminals)) {
            setTerminals(storedTerminals)
          } else {
            setTerminals([])
          }

          const storedFranchiseUsers = sessionService.franchiseUsers
          if (storedFranchiseUsers && storedFranchiseUsers.length > 0) {
            setMembers(storedFranchiseUsers)
          } else {
            const users = await apiService.getFranchiseUsers()
            setMembers(users)
          }
        } catch (error) {
          logger.error("Error fetching data:", error)
        } finally {
          setIsLoading(false)
        }
      } else {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [sessionService, apiService, logger])

  // Auto-close success message after 3 seconds
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false)
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [showSuccessMessage])

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setShowSuccessMessage(true)
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    setEditedMember(null)
  }

  const handleCreateMember = async (newUser: NewUser) => {
    setIsCreatingUser(true)
    setIsProcessing(true)
    setProcessingMessage("Creating member...")
    try {
      // Create the new user
      const createdUser = await apiService.createMember(newUser)
      logger.info("User Created Successfully:", createdUser)

      // Update the session with the enriched user data
      sessionService.updateFranchiseUser(createdUser)

      // Update the members state with the enriched user data
      setMembers((prevMembers) => [...prevMembers, createdUser])

      setShowForm(false)
      showSuccess("Member Created Successfully!")
    } catch (error) {
      logger.error("Error creating User:", error)
      alert("Email or phone exists. Try a different one.")
    } finally {
      setIsCreatingUser(false)
      setIsProcessing(false)
    }
  }

  const handleEditMember = (member: User) => {
    setEditedMember(member)
    setEditMode(true)
  }

  const handleSaveMember = async () => {
    if (!editedMember) return

    setIsSaving(true)
    setIsProcessing(true)
    setProcessingMessage("Saving changes...")

    try {
      // Extract the email of the user being updated
      const userEmail = editedMember.email

      // Call the API to update the user
      const updatedMemberResponse = await apiService.updateMember({
        email: userEmail,
        isActive: editedMember.isActive,
        terminals: editedMember.terminals.map((terminal) => terminal.terminalId),
        role: editedMember.role,
        address: editedMember.address,
      })

      // Construct the updated member object using the email from editedMember and the rest from the API response
      const updatedMember: User = {
        ...editedMember,
        ...updatedMemberResponse,
        email: userEmail,
      }

      // Update the session with the updated user
      sessionService.updateFranchiseUser(updatedMember)

      // Update the member list in the state
      setMembers((prevMembers) => {
        const updatedMembers = prevMembers.map((member) => (member.email === userEmail ? updatedMember : member))
        return updatedMembers
      })

      setEditMode(false)
      setEditedMember(null)
      showSuccess("Member Updated Successfully!")
    } catch (error) {
      logger.error("Error updating User:", error)
      alert("Failed to update User. Please try again.")
    } finally {
      setIsSaving(false)
      setIsProcessing(false)
    }
  }

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEditedMember((prev) => (prev ? { ...prev, isActive: e.target.value === "Active" } : prev))
  }

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEditedMember((prev) => (prev ? { ...prev, role: Number.parseInt(e.target.value) } : prev))
  }

  const handleTerminalChange = (terminalId: string, isChecked: boolean) => {
    setEditedMember((prev) => {
      if (!prev) return prev

      const updatedTerminals = isChecked
        ? [...prev.terminals, terminals.find((t) => t.terminalId === terminalId)!]
        : prev.terminals.filter((t) => t.terminalId !== terminalId)

      return { ...prev, terminals: updatedTerminals }
    })
  }

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof typeof editedMember.address) => {
    setEditedMember((prev) =>
      prev
        ? {
            ...prev,
            address: {
              ...prev.address,
              [field]: e.target.value,
            },
          }
        : prev,
    )
  }

  const getTerminalNames = (terminals: Terminal[] | undefined): string => {
    if (!terminals || terminals.length === 0) return "N/A"
    return terminals.map((terminal) => terminal.name).join(", ")
  }

  const getRolesBelowCurrentRole = (currentRole: FranchiseRole): FranchiseRole[] => {
    const roles = Object.values(FranchiseRole).filter((role) => typeof role === "number" && role > currentRole)
    return roles as FranchiseRole[]
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-200">
        <ClipLoader size={50} color={COLORS.BLUE} />
        <span className="ml-4 text-blue-500">Loading...</span>
      </div>
    )
  }

  // Filter members based on search query
  const filteredMembers = members.filter((member) => {
    const searchString = [
      member.firstname,
      member.lastname,
      member.email,
      member.phone || "",
      getTerminalNames(member.terminals),
      member.address
        ? `${member.address.street} ${member.address.city} ${member.address.state} ${member.address.postalCode}`
        : "",
      getRoleString(member.role),
    ]
      .join(" ")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim()

    return searchString.includes(searchQuery.toLowerCase().trim())
  })

  if (!user) {
    return <div>Loading user details...</div>
  }

  return (
    <div className="pt-2 px-2 pb-6 w-full max-w-full">
      {/* Loading Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <ClipLoader size={50} color={COLORS.BLUE} />
            <span className="mt-4 text-blue-500 font-medium">{processingMessage}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center max-w-md">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
              <svg
                data-automation-id="memberdashboard-success-message-tickmark-img"
                id="memberdashboard-success-message-tickmark-img"
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-center">{successMessage}</h2>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2
          data-automation-id="memberdashboard-list-of-members-text"
          id="memberdashboard-list-of-members-text"
          className="text-2xl font-semibold text-gray-800"
        >
          List of Members
        </h2>
        <CreateButton
          data-automation-id="memberdashboard-form-create-newsuser-button"
          id="memberdashboard-form-create-newsuser-button"
          setShowForm={setShowForm}
          buttonText="+ Create User"
        />
      </div>
      {showForm && (
        <div className="fixed inset-0 z-50 bg-opacity-40 backdrop-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
            <CreateFranchiseUserForm
              onSubmit={handleCreateMember}
              onClose={() => setShowForm(false)}
              terminals={terminals}
              loggedInUserRole={user.role}
              isCreatingUser={isCreatingUser}
            />
          </div>
        </div>
      )}
      {filteredMembers.length === 0 ? (
        <p className="text-center text-gray-500 mt-4">No members found.</p>
      ) : (
        <div className="mt-6" style={{ maxHeight: "calc(90vh - 180px)" }}>
          {/* Wrapper to apply border radius */}
          <div className="rounded-lg overflow-hidden shadow border border-gray-200 bg-white">
            <div className="max-h-[500px] overflow-y-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-white">
                  <tr>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      Name
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      E-mail
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      Phone
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      Terminals
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      Address
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      Role
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                      Status
                    </th>
                    {user.role !== FranchiseRole.USER && (
                      <th className="py-3 px-4 text-left text-xs font-bold text-black-500 uppercase tracking-wider border-l border-r border-gray-400 sticky top-0 z-10 bg-gray-300">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-black">
                  {filteredMembers.map((member) => (
                    <tr key={member.email} className="bg-white">
                      <td className="py-2 pl-2 text-sm text-gray-900 border-r border-gray-300 text-center">
                        {member.firstname} {member.lastname}
                      </td>
                      <td className="py-2 pl-2 text-sm text-black-500 border-r border-gray-300 text-center">
                        {member.email}
                      </td>
                      <td className="py-2 pl-2 text-sm text-black-500 border-r border-gray-300 text-center">
                        {member.phone.primary}
                      </td>
                      <td className="py-2 pl-2 text-sm text-black-500 border-r border-gray-300 text-center">
                        {getTerminalNames(member.terminals)}
                      </td>
                      <td className="py-2 pl-2 text-sm text-black-500 border-r border-gray-300 text-center">
                        {member.address
                          ? `${member.address.street}, ${member.address.city}, ${member.address.state}, ${member.address.postalCode}`
                          : "N/A"}
                      </td>
                      <td className="py-2 pl-2 text-sm text-black-500 border-r border-gray-300 text-center">
                        {getRoleString(member.role)}
                      </td>
                      <td className="py-2 pl-2 text-sm border-r border-white-300">
                        <span
                          className="inline-flex items-center justify-center w-20 h-6 text-xs font-semibold rounded-full text-white"
                          style={{
                            backgroundColor: member.isActive ? COLORS.ACTIVE : COLORS.MAINTENANCE,
                          }}
                        >
                          {member.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {user.role !== FranchiseRole.USER && (
                        <td className="py-2 pl-2 text-sm space-x-2">
                          {member.role !== FranchiseRole.OWNER && (
                            <>
                              <EditButton
                                id="memberdashboard-form-edit-button"
                                onClick={() => handleEditMember(member)}
                              />
                              <DeleteButton id="memberdashboard-delete-user-button" disabled={true} />
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editMode && editedMember && (
        <div className="fixed inset-0 z-50 bg-opacity-40 backdrop-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md mx-auto overflow-y-auto max-h-screen">
            <div className="p-6">
              <h2
                data-automation-id="memberdashboard-form-edit-user-text"
                id="memberdashboard-form-edit-user-text"
                className="text-xl font-semibold mb-4"
              >
                Edit User
              </h2>
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <label
                    data-automation-id="memberdashboard-form-status-label"
                    id="memberdashboard-form-status-label"
                    className="block text-sm font-medium mb-1"
                  >
                    Status
                  </label>
                  <select
                    data-automation-id="memberdashboard-form-dropdown-status-select"
                    id="memberdashboard-form-dropdown-status-select"
                    value={editedMember.isActive ? "Active" : "Inactive"}
                    onChange={handleStatusChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label
                    data-automation-id="memberdashboard-form-role-label"
                    id="memberdashboard-form-role-label"
                    className="block text-sm font-medium mb-1"
                  >
                    Role
                  </label>
                  <select
                    data-automation-id="memberdashboard-dropdown-role-select"
                    id="memberdashboard-dropdown-role-select"
                    value={editedMember.role}
                    onChange={handleRoleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    {getRolesBelowCurrentRole(user.role).map((role) => (
                      <option key={role} value={role}>
                        {getRoleString(role)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Terminals */}
                <div>
                  <label
                    data-automation-id="memberdashboard-form-terminal-label"
                    id="memberdashboard-form-terminal-label"
                    className="block text-sm font-medium mb-1"
                  >
                    Terminals
                  </label>
                  <div className="space-y-2">
                    {terminals.map((terminal) => (
                      <div key={terminal.terminalId} className="flex items-center">
                        <input
                          type={TYPES.CHECKBOX}
                          id={terminal.terminalId}
                          checked={editedMember.terminals.some((t) => t.terminalId === terminal.terminalId)}
                          onChange={(e) => handleTerminalChange(terminal.terminalId, e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />

                        <label
                          data-automation-id="memberdashboard-form-terminalname-label"
                          id="memberdashboard-form-terminalname-label"
                          htmlFor={terminal.terminalId}
                          className="ml-2 block text-sm text-gray-900"
                        >
                          {terminal.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Address Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      data-automation-id="memberdashboard-form-address-street-label"
                      id="memberdashboard-form-address-street-label"
                      className="block text-sm font-medium mb-1"
                    >
                      Street
                    </label>
                    <input
                      data-automation-id="memberdashboard-form-inputbox-address-street-input"
                      id="memberdashboard-form-inputbox-address-street-input"
                      type={TYPES.TEXT}
                      value={editedMember.address?.street || ""}
                      onChange={(e) => handleAddressChange(e, "street")}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label
                      data-automation-id="memberdashboard-form-address-city-label"
                      id="memberdashboard-form-address-city-label"
                      className="block text-sm font-medium mb-1"
                    >
                      City
                    </label>
                    <input
                      data-automation-id="memberdashboard-form-inputbox-address-city-input"
                      id="memberdashboard-form-inputbox-address-city-input"
                      type={TYPES.TEXT}
                      value={editedMember.address?.city || ""}
                      onChange={(e) => handleAddressChange(e, "city")}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      data-automation-id="memberdashboard-form-address-state-label"
                      id="memberdashboard-form-address-state-label"
                      className="block text-sm font-medium mb-1"
                    >
                      State
                    </label>
                    <input
                      data-automation-id="memberdashboard-form-inputbox-address-state-input"
                      id="memberdashboard-form-inputbox-address-state-input"
                      type={TYPES.TEXT}
                      value={editedMember.address?.state || ""}
                      onChange={(e) => handleAddressChange(e, "state")}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label
                      data-automation-id="memberdashboard-form-address-postalcode-label"
                      id="memberdashboard-form-address-postalcode-label"
                      className="block text-sm font-medium mb-1"
                    >
                      Postal Code
                    </label>
                    <input
                      data-automation-id="memberdashboard-form-inputbox-address-postalcode-input"
                      id="memberdashboard-form-inputbox-address-postalcode-input"
                      type={TYPES.TEXT}
                      value={editedMember.address?.postalCode || ""}
                      onChange={(e) => handleAddressChange(e, "postalCode")}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 mt-6">
                <ActionButton
                  variant={ActionButtonVariant.CANCEL}
                  data-automation-id="memberdashboard-close-edit-form-button"
                  id="memberdashboard-close-edit-form-button"
                  onClick={handleCancelEdit}
                />
                <ActionButton
                  variant={ActionButtonVariant.SAVE}
                  data-automation-id="memberdashboard-save-edited-detail-button"
                  id="memberdashboard-save-edited-detail-button"
                  onSubmit={handleSaveMember}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemberDashboard